import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usePlayer from '../../contexts/usePlayer';
import useAuth from '../../contexts/useAuth';
import { ItemProvider, useItem } from '../../contexts/ItemContext';
import TranscriptArea from '../../components/item/TranscriptArea';
import ExpandableBottomPanel from '../../components/item/ExpandableBottomPanel';
import { ItemPlayBar } from '../../components/item';
import ChaptersDrawer from '../../components/ChaptersDrawer';
import VoiceSelector from '../../components/VoiceSelector';
import DeleteButton from '../../components/DeleteButton';
import ResetButton from '../../components/ResetButton';

function Item() {
  const {
    content: displayItem,
    sentences,
    timeMap,
    isPlaylist,
    playlistItems,
    currentPosition,
    currentItem,
    setPlaylistPosition,
  } = useItem();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { nowPlaying, nowPlayingId, currentTime, currentPlaylist } =
    usePlayer();

  const [showAdminControls, setShowAdminControls] = useState(false);
  const [voice, setVoice] = useState('');
  const [isChaptersOpen, setIsChaptersOpen] = useState(false);

  const playBarVisible =
    !!nowPlaying &&
    nowPlayingId !== displayItem.id &&
    currentPlaylist?.id !== displayItem.id;

  const isNowPlaying = isPlaylist
    ? nowPlaying && currentItem && nowPlaying.id === currentItem.id
    : nowPlaying && nowPlaying.id === displayItem.id;

  const itemToDisplay = isPlaylist
    ? currentItem || displayItem
    : isNowPlaying && nowPlaying
      ? nowPlaying
      : displayItem;

  const displayTitle =
    isPlaylist && currentItem
      ? `${displayItem.title} | ${currentItem.title}`
      : itemToDisplay.title;
  const coverImageUrl =
    isPlaylist && currentItem
      ? currentItem.imageUrl || displayItem.imageUrl
      : itemToDisplay.imageUrl;

  const initialScrollTime = isNowPlaying
    ? currentTime
    : (itemToDisplay.secondsListened ?? 0);

  const transcriptContentId =
    isPlaylist && currentItem ? currentItem.id : displayItem.id;

  useEffect(() => {
    if (itemToDisplay?.voice) {
      setVoice(itemToDisplay.voice);
    }
  }, [itemToDisplay?.voice]);

  const handleVoiceChange = async (newVoice: string) => {
    setVoice(newVoice);
    try {
      await fetch('/api/content/regenerate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentId: displayItem.id, voice: newVoice }),
      });
    } catch (err) {
      console.error('Failed to update voice', err);
    }
  };

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {user?.isAdmin && (
        <button
          onClick={() => setShowAdminControls(!showAdminControls)}
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            width: 40,
            height: 40,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            zIndex: 10,
          }}
          aria-label="Toggle admin controls"
        />
      )}

      <TranscriptArea
        contentId={transcriptContentId}
        sentences={sentences}
        timeMap={timeMap}
        coverImageUrl={coverImageUrl}
        coverAlt={displayTitle}
        initialTime={initialScrollTime}
      />

      {showAdminControls && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            padding: 10,
            justifyContent: 'center',
            borderTop: '1px solid var(--border)',
            background: 'var(--background)',
          }}
        >
          <VoiceSelector
            voice={voice || 'openai-onyx'}
            setVoice={handleVoiceChange}
          />
          <ResetButton item={itemToDisplay} />
          <DeleteButton item={itemToDisplay} />
          {itemToDisplay.type === 'content' && (
            <button
              onClick={() => navigate(`/id/${itemToDisplay.id}/sentences`)}
              className="pill-button"
            >
              Sentences
            </button>
          )}
        </div>
      )}

      <ExpandableBottomPanel
        item={itemToDisplay}
        displayTitle={displayTitle}
        isPlaylist={isPlaylist}
        onChaptersClick={isPlaylist ? () => setIsChaptersOpen(true) : undefined}
      />
      <div style={{ paddingTop: 6 }}>
        <ItemPlayBar
          item={itemToDisplay}
          playlist={isPlaylist ? displayItem : undefined}
        />
      </div>

      {playBarVisible && <div style={{ flexShrink: 0, height: 65 }} />}

      {isPlaylist && (
        <ChaptersDrawer
          isOpen={isChaptersOpen}
          onClose={() => setIsChaptersOpen(false)}
          items={playlistItems}
          currentPosition={currentPosition}
          onSelectChapter={setPlaylistPosition}
        />
      )}
    </div>
  );
}

export default function ItemPage() {
  return (
    <ItemProvider>
      <Item />
    </ItemProvider>
  );
}
